// TaskDetailComponent.js
import React, { useEffect, useState, useRef, act } from "react";
import { BASE_URL } from "utils/constant";
import { taskApi } from "api/tasks/taskApi";
import AddSubtaskModal from "./AddSubtaskModal";
import { getInitials } from "utils/tasks/taskHelpers";
import CalendarPicker from "./CalenderPicker";
import { MdDelete, MdEdit } from "react-icons/md";
import { useAlert } from "components/UI_components/Alert";
import { format } from "date-fns";
import { FaSyncAlt } from "react-icons/fa";
import { UserAPI } from "api/dashboard/service";
import {
  Download,
  FileText,
  Image,
  Paperclip,
  Trash2,
  X,
  MoreVertical,
} from "lucide-react";
import FileViewer from "components/FileViewer";
import ImageAnnotatorModal from "components/ImageAnnotatorModal";
import EllipsisMenu from "components/UI_components/EllipsisMenu";
import DeleteConfirmationModal from "../Snags/components/DeleteConfirmationModal";
import { GoAlert } from "react-icons/go";
/* ---------- helpers ---------- */
const priorityText = (n) =>
  ({ 1: "High", 2: "Medium", 3: "Low" })[n] || "Medium";
const toArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw?.subtasks) return raw.subtasks;
  if (raw?.data?.results) return raw.data.results;
  if (raw?.data) return raw.data;
  return [];
};
const normalizeAssigneeList = (assignees) =>
  Array.isArray(assignees) ? assignees : [];
const initialsOf = (f = "", l = "") =>
  (f[0] || "").toUpperCase() + (l[0] || "").toUpperCase();
const todayISO = () => new Date().toISOString().slice(0, 10);

const CustomCalendar = ({ selectedDate, onDateSelect, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(selectedDate || "");

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleDateClick = (day) => {
    if (day) {
      const clickedDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day,
      );
      const dateString = clickedDate.toISOString().split("T")[0];
      setEndDate(dateString);
    }
  };

  const handleOk = () => {
    if (endDate) {
      onDateSelect(endDate);
    }
    onClose();
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day &&
      currentMonth.getFullYear() === today.getFullYear() &&
      currentMonth.getMonth() === today.getMonth() &&
      day === today.getDate()
    );
  };

  const isSelected = (day) => {
    if (!day || !endDate) return false;
    const selectedDateObj = new Date(endDate);
    return (
      currentMonth.getFullYear() === selectedDateObj.getFullYear() &&
      currentMonth.getMonth() === selectedDateObj.getMonth() &&
      day === selectedDateObj.getDate()
    );
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg p-4 z-50 w-80">
      {/* Date inputs */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start date"
            className="w-full p-2 border rounded text-sm"
          />
        </div>
        <div className="flex-1">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End date"
            className="w-full p-2 border rounded text-sm"
          />
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
          ×
        </button>
      </div>

      {/* Calendar header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          ←
        </button>
        <h3 className="font-medium">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          onClick={() => navigateMonth(1)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          →
        </button>
      </div>

      {/* Days of week header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {daysOfWeek.map((day, index) => (
          <div
            key={index}
            className="text-center text-sm font-medium text-gray-500 p-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {days.map((day, index) => (
          <button
            key={index}
            onClick={() => handleDateClick(day)}
            disabled={!day}
            className={`
              p-2 text-sm rounded hover:bg-gray-100 
              ${!day ? "invisible" : ""}
              ${isToday(day) ? "bg-blue-100 text-blue-600" : ""}
              ${
                isSelected(day)
                  ? "bg-purple-100 text-purple-600 ring-2 ring-purple-300"
                  : ""
              }
            `}
          >
            {day}
          </button>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleOk}
          className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
        >
          OK
        </button>
      </div>
    </div>
  );
};

/* ---------- component ---------- */
const TaskDetailComponent = ({
  task,
  onClose,
  refetchTasks,
  refetchSubtasks,
  onOpenSubtask,
  isVisible = true,
}) => {
  const { addAlert } = useAlert();
  /* ---------- state ---------- */
  const [activeTab, setActiveTab] = useState("Updates");
  const [userData, setUserData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [startDate, setStartDate] = useState(task.start_date || null);
  const [endDate, setEndDate] = useState(task.end_date || null);
  const [isUpdatingDates, setIsUpdatingDates] = useState(false);
  const [file, setFile] = useState(null);
  const [annotatingFile, setAnnotatingFile] = useState(null);
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [creatingSnagUpdateId, setCreatingSnagUpdateId] = useState(null);
  const [editedDates, setEditedDates] = useState({
    start_date: task.startDate || "",
    end_date: task.endDate || "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [taskInfo, setTaskInfo] = useState();
  /* subtasks */
  const [subtasks, setSubtasks] = useState(task.subtasks || []);
  const [loadingSub, setLoadingSub] = useState(true);
  const [trigger, setTrigger] = useState(0);

  /* updates */
  const [updates, setUpdates] = useState([]);
  const [loadingUpd, setLoadingUpd] = useState(false);
  const [newComment, setNewComment] = useState("");
  const fileInputRef = useRef();
  const imageInputRef = useRef();
  const filesTabInputRef = useRef();
  const [editingId, setEditingId] = useState(null);
  const [hoveredUpdateId, setHoveredUpdateId] = useState(null);

  /* members / assign */
  const [assignees, setAssignees] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [siteMembers, setSiteMembers] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [canAddFiles, setCanAddFiles] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [tempTaskName, setTempTaskName] = useState(task.name || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [selectedViewerFile, setSelectedViewerFile] = useState(null);
  const [linkedDocs, setLinkedDocs] = useState([]);
  const [linkedDocsLoading, setLinkedDocsLoading] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [taskActionsOpen, setTaskActionsOpen] = useState(false);
  const [hasTaskAuthority, setHasTaskAuthority] = useState(false);
  const [isTaskLinked, setIsTaskLinked] = useState(false);

  const dropdownRef = useRef(null);
  const taskActionsRef = useRef(null);

  /* ---------- auth ---------- */
  const token = localStorage.getItem("access");
  const siteId = localStorage.getItem("site");
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [showUpdates, setShowUpdates] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const [myApiUserpic, setMyApiUserpic] = useState(null);
  const canEditTaskMeta = hasTaskAuthority;
  const formatTaskDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    return (
      url.toLowerCase().endsWith(".jpg") ||
      url.toLowerCase().endsWith(".jpeg") ||
      url.toLowerCase().endsWith(".png") ||
      url.toLowerCase().endsWith(".gif") ||
      url.toLowerCase().endsWith(".webp") ||
      /\.(jpg|jpeg|png|gif|webp)/i.test(url)
    );
  };

  const isImageAttachment = (update) => {
    if (!update) return false;
    const mimeCandidates = [
      update?.file_type,
      update?.mime_type,
      update?.content_type,
      update?.file_mime,
      update?.file_content_type,
    ]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase());

    if (mimeCandidates.some((v) => v.includes("image/"))) return true;

    return isImageUrl(update?.file);
  };

  const normalizeMaybeUrl = (maybeUrl) => {
    if (!maybeUrl) return null;
    const u = String(maybeUrl);
    if (
      u.startsWith("http://") ||
      u.startsWith("https://") ||
      u.startsWith("data:") ||
      u.startsWith("blob:")
    ) {
      return u;
    }
    return `${BASE_URL}${u.startsWith("/") ? "" : "/"}${u}`;
  };

  const fileNameFromUrl = (url, fallback = "Document") => {
    if (!url) return fallback;
    try {
      const clean = String(url).split("?")[0];
      const last = clean.split("/").pop();
      return last || fallback;
    } catch {
      return fallback;
    }
  };

  const handleDownload = async (url, name) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name || fileNameFromUrl(url);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: open in new tab if fetch fails
      window.open(url, "_blank");
    }
  };

  const isVideoUrl = (url) => {
    if (!url) return false;
    return (
      url.toLowerCase().endsWith(".mp4") ||
      url.toLowerCase().endsWith(".mov") ||
      url.toLowerCase().endsWith(".avi") ||
      url.toLowerCase().endsWith(".wmv") ||
      url.toLowerCase().endsWith(".flv") ||
      url.toLowerCase().endsWith(".mkv") ||
      url.toLowerCase().endsWith(".webm") ||
      /\.(mp4|mov|avi|wmv|flv|mkv|webm)/i.test(url)
    );
  };

  const getUpdateCommentText = (update) => {
    const comment = update?.comment;
    return typeof comment === "string" ? comment.trim() : "";
  };

  const hasImageAndComment = (update) => {
    const comment = getUpdateCommentText(update);
    const hasImage = isImageAttachment(update);
    return Boolean(comment) && hasImage;
  };

  const readErrorMessage = async (response, fallback) => {
    const text = await response.text().catch(() => "");
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text);
      return (
        parsed?.detail ||
        parsed?.message ||
        Object.values(parsed || {})?.[0]?.[0] ||
        fallback
      );
    } catch {
      return fallback;
    }
  };

  const urlToImageFile = async (url, fallbackName = "update-image.jpg") => {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Unable to load update image");
    const blob = await response.blob();
    const mime = blob.type || "image/jpeg";
    const ext =
      mime === "image/png"
        ? "png"
        : mime === "image/webp"
          ? "webp"
          : mime === "image/gif"
            ? "gif"
            : "jpg";
    const fileName = fallbackName.includes(".")
      ? fallbackName
      : `${fallbackName}.${ext}`;
    return new File([blob], fileName, { type: mime });
  };

  const handleGenerateSnagFromUpdate = async (update) => {
    if (!hasImageAndComment(update)) {
      addAlert(
        "Snag generation is available only for updates with both an image and a comment.",
        "warning",
      );
      return;
    }

    const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!organizationId) {
      addAlert(
        "Organization not found. Please refresh and try again.",
        "error",
      );
      return;
    }

    setCreatingSnagUpdateId(update.id);
    try {
      const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
      const resolvedSiteId =
        task?.site ||
        task?.site_id ||
        taskInfo?.site ||
        taskInfo?.site_id ||
        siteData?.id ||
        localStorage.getItem("site");

      if (!resolvedSiteId) {
        throw new Error("Site is missing for this task.");
      }

      const issueImage = await urlToImageFile(
        update.file,
        `task-update-${update.id}-snag.jpg`,
      );

      const formData = new FormData();
      const taskNameForCategory = task?.name || taskInfo?.name || "Task";
      formData.append("title", `Task Update: ${task?.name || "Untitled Task"}`);
      formData.append("description", getUpdateCommentText(update));
      formData.append("location", taskInfo?.location || task?.location || "");
      formData.append("category", taskNameForCategory);
      formData.append("priority", "2");
      formData.append("status", "Draft");
      formData.append("site", String(resolvedSiteId));
      formData.append("issue_image", issueImage);
      formData.append("task_update_id", String(update.id));

      const res = await fetch(
        `${BASE_URL}/snag-management/org/${organizationId}/snags/create/`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );

      if (!res.ok) {
        const message = await readErrorMessage(
          res,
          `Failed to create snag (${res.status})`,
        );
        throw new Error(message);
      }
      reloadUpdates();
      addAlert("Snag generated from update successfully.", "success");
    } catch (e) {
      addAlert(e.message || "Failed to generate snag from update.", "error");
    } finally {
      setCreatingSnagUpdateId(null);
    }
  };

  const handleTaskDelete = () => {
    setShowConfirmDelete(true);
  };

  const fetchTask = async (taskId) => {
    try {
      const response = await fetch(`${BASE_URL}/task/get-task/${taskId}/`, {
        headers: authHeaders,
      });
      if (!response.ok) {
        throw new Error("Failed to fetch task details");
      }
      const data = await response.json();
      setStartDate(data.data.start_date || null);
      setEndDate(data.data.end_date || null);
      // console.log("Fetched task data:", data);
      setTaskInfo(data.data);
    } catch (error) {
      console.error("Error fetching task:", error);
      addAlert("Failed to fetch task details", "error");
    }
  };

  const fetchLinkedDocuments = async (taskId) => {
    if (!taskId) return [];
    setLinkedDocsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/task/task-documents/${taskId}/`, {
        method: "GET",
        headers: authHeaders,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          json?.detail || json?.message || "Failed to fetch linked documents",
        );
      }
      const list = toArray(json);
      const normalized = list
        .map((d, idx) => {
          const rawUrl =
            d?.diagram ||
            d?.file ||
            d?.document ||
            d?.url ||
            d?.document_url ||
            d?.file_url ||
            null;
          const url = normalizeMaybeUrl(rawUrl);
          const name =
            d?.design_name ||
            d?.name ||
            d?.title ||
            fileNameFromUrl(url, `Document #${d?.id ?? idx + 1}`);
          return {
            id: d?.id ?? `${idx}`,
            name,
            url,
            design_status: d.design_status,
            created_by: d.created_by,
            last_updated_on: d?.last_updated_on,
            raw: d,
          };
        })
        .filter((x) => x.url);
      setLinkedDocs(normalized);
      return normalized;
    } catch (e) {
      console.error("Error fetching linked documents:", e);
      setLinkedDocs([]);
      return [];
    } finally {
      setLinkedDocsLoading(false);
    }
  };

  const onDeleteTask = async () => {
    try {
      const response = await fetch(`${BASE_URL}/task/delete-task/${task.id}/`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete task");
      }
      addAlert("Task deleted successfully", "success");
      onClose(); // Close the task detail view
      refetchTasks(task.task_group || taskInfo?.task_group); // Refresh the task list
    } catch (error) {
      console.error("Failed to delete task:", error);
      addAlert(error.message || "Failed to delete task", "error");
    } finally {
      setShowConfirmDelete(false); // Hide confirmation dialog
    }
  };

  useEffect(() => {
    fetchTask(task.id);
    checkIfTaskIsBoQLinked();
  }, [task.id]);

  /* Check if task is linked to BoQ */
  const checkIfTaskIsBoQLinked = async () => {
    try {
      const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
      const siteId = siteData?.id || localStorage.getItem("site");
      if (!siteId || !task?.task_group) return;

      const response = await fetch(
        `${BASE_URL}/task/get-all-task-groups/?site_id=${siteId}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      const taskGroups = Array.isArray(data) ? data : data?.data || [];
      const taskGroupData = taskGroups.find((tg) => tg.id === task.task_group);
      if (taskGroupData) {
        setIsTaskLinked(taskGroupData.is_group_linked === true);
      }
    } catch (error) {
      console.error("Error checking task BoQ linkage:", error);
      setIsTaskLinked(false);
    }
  };

  // fetch user data from localStorage
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        setUserData(parsedUser);
      } catch (error) {
        console.error("Failed to parse user data:", error);
      }
    }
  }, [localStorage.getItem("user")]);
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        taskActionsRef.current &&
        !taskActionsRef.current.contains(event.target)
      ) {
        setTaskActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const fetchSiteMembersForAuthority = async () => {
      try {
        const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
        if (!siteData?.id || !user?.user) return;
        const response = await taskApi.getSiteMembers(siteData.id);
        if (response?.status) {
          const currentMember = response.data.find(
            (member) => member.user_id === user.user,
          );
          setHasTaskAuthority(
            currentMember?.create_taskgroup ||
              currentMember?.is_admin ||
              currentMember?.joined_as === "Owner",
          );
        }
      } catch (e) {
        setHasTaskAuthority(false);
      }
    };

    fetchSiteMembersForAuthority();
  }, [user?.user]);

  useEffect(() => {
    if (hasTaskAuthority) return;
    setDropdownOpen(false);
    setTaskActionsOpen(false);
  }, [hasTaskAuthority]);

  /* ---------- fetch subtasks once ---------- */
  useEffect(() => {
    (async () => {
      setLoadingSub(true);
      try {
        const res = await fetch(`${BASE_URL}/task/get-all-subtasks/`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ task_id: task.id }),
        });
        const list = toArray(await res.json());
        setSubtasks(
          list.map((st) => {
            const assigneeList = normalizeAssigneeList(st.assign_to);
            return {
              id: st.subtask_id ?? st.id,
              task_id: st.task_id ?? task.id,
              subtask_name: st.subtask_name ?? st.name ?? "(no-title)",
              subtask_details: st.subtask_details || "",
              status: st.is_completed ? "Completed" : "Ongoing",
              priority: priorityText(st.priority),
              start_date: st.start_date || "",
              end_date: st.end_date || "",
              assign_to: assigneeList,
              assignee: assigneeList,
              is_completed: st.is_completed || false,
            };
          }),
        );
      } catch {
        setSubtasks([]);
      } finally {
        setLoadingSub(false);
      }
    })();
  }, [task.id, trigger]);

  /* ---------- fetch task members once ---------- */
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE_URL}/task/get-task-members/`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({ task_id: task.id }),
        });
        const cur = toArray(await r.json());
        setAssignees(
          cur.map((m) => ({
            id: m.id,
            member_id: m.member_id,
            first_name: m.first_name,
            last_name: m.last_name,
            userpic: m.userpic,
            initials: initialsOf(m.first_name, m.last_name),
            color: "#5b6ad0",
          })),
        );

        const isApprover = cur.some(
          (m) => m.is_approver && m.user_id === user.user,
        );
        setIsApprover(isApprover);
      } catch {
        /* ignore */
      }
    })();
  }, [task.id, trigger]);

  /* ---------- load updates whenever tab becomes active ---------- */
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [updates]);

  useEffect(() => {
    if (activeTab !== "Updates") return;
    (async () => {
      try {
        if (updates.length === 0) {
          setLoadingUpd(true);
        }
        const res = await fetch(`${BASE_URL}/task/tasks/${task.id}/updates/`, {
          headers: authHeaders,
        });
        const data = await res.json();
        setUpdates(data.data.reverse() || []);
      } catch {
        // setUpdates([]); // Don't clear updates on error to keep existing ones visible
      } finally {
        setLoadingUpd(false);
      }
    })();
  }, [activeTab, task.id, trigger]);

  /* ---------- helpers ---------- */
  const resetCommentBox = () => {
    setNewComment("");
    setFile(null);
    setEditingId(null);
    try {
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (imageInputRef.current) imageInputRef.current.value = "";
    } catch {
      /* ignore */
    }
  };
  const reloadUpdates = () => {
    setTrigger((prev) => prev + 1);
  };

  // Derive current user's avatar from API updates when available
  const currentUserName = `${userData.first_name || ""} ${
    userData.last_name || ""
  }`.trim();
  const myUserpic = React.useMemo(() => {
    if (!updates?.length || !currentUserName) return null;
    for (const u of updates) {
      const name = u.created_by?.includes("|")
        ? u.created_by.split("|")[1]
        : u.created_by;
      if (name && name.trim() === currentUserName) {
        return u.userpic || null;
      }
    }
    return null;
  }, [updates, currentUserName]);

  // Fetch current user's userpic via API
  useEffect(() => {
    (async () => {
      try {
        const res = await UserAPI.getUserDetails();
        if (res?.status) {
          setMyApiUserpic(res?.data?.userpic || null);
        }
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);

  const handleCalendarDateSelect = (dateString) => {
    setEditedDates((prev) => ({
      ...prev,
      end_date: dateString,
    }));
    setShowCalendar(false);
  };

  // const handleSaveDates = async () => {
  //   setIsSaving(true);
  //   try {
  //     await fetch(`${BASE_URL}/task/tasks/${task.id}/`, {
  //       method: "PATCH",
  //       headers: authHeaders,
  //       body: JSON.stringify({
  //         start_date: editedDates.start_date,
  //         end_date: editedDates.end_date,
  //       }),
  //     });
  //     setIsEditing(false);
  //   } catch (error) {
  //     console.error("Failed to update dates:", error);
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleDateChange = async (newStartDate, newEndDate) => {
    if (!hasTaskAuthority) return;
    if (!newStartDate || !newEndDate) return;
    setIsUpdatingDates(true);
    // console.log("Updating dates:", newStartDate, newEndDate);
    try {
      const res = await fetch(`${BASE_URL}/task/update-task/${task.id}/`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({
          start_date: newStartDate ? format(newStartDate, "yyyy-MM-dd") : null,
          end_date: newEndDate ? format(newEndDate, "yyyy-MM-dd") : null,
        }),
      });
      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to update task. You must have Task Authority Permission",
          "error",
        );
        setStartDate(task.start_date || null);
        setEndDate(task.end_date || null);
        return;
      }

      setStartDate(newStartDate);
      setEndDate(newEndDate);
      refetchTasks(task.task_group || taskInfo?.task_group);
    } catch (e) {
      console.error("Update dates failed", e);
      addAlert("Could not update dates.", "error");
    } finally {
      setIsUpdatingDates(false);
    }
  };

  /* ---------- CRUD update ---------- */
  const handleSubmitComment = async () => {
    const trimmed = newComment.trim();
    if (!trimmed && !file) return;
    if (isSubmittingComment) return;
    setIsSubmittingComment(true);
    try {
      if (editingId === null) {
        const payload = new FormData();
        if (trimmed) {
          payload.append("comment", trimmed);
        }
        if (file) {
          payload.append("file", file);
        }

        await fetch(`${BASE_URL}/task/tasks/${task.id}/updates/create/`, {
          method: "POST",
          headers: {
            Authorization: authHeaders.Authorization,
          },
          body: payload,
        });
      } else {
        const res = await fetch(
          `${BASE_URL}/task/tasks/updates/${editingId}/`,
          {
            method: "PATCH",
            headers: authHeaders,
            body: JSON.stringify({ comment: trimmed }),
          },
        );
        const data = await res.json();
        if (data.detail) {
          addAlert(data.detail, "error");
          return;
        }
      }
      reloadUpdates();
      resetCommentBox();
    } catch (e) {
      console.error("Comment save failed", e);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/task/tasks/updates/${id}/delete/`, {
        method: "DELETE",
        headers: authHeaders,
      });
      const data = await res.json();
      if (data.detail) {
        addAlert(data.detail, "error");
        return;
      }
      setUpdates((u) => u.filter((x) => x.id !== id));
    } catch (e) {
      console.error("Delete failed", e);
    }
  };

  /* ---------- assign helpers ---------- */
  const handlePlusClick = async () => {
    if (!hasTaskAuthority) return;
    if (!dropdownOpen) {
      try {
        const r = await fetch(`${BASE_URL}/team/list-site-members/${siteId}/`, {
          headers: authHeaders,
        });
        setSiteMembers(toArray(await r.json()));
      } catch {
        setSiteMembers([]);
      }
    }
    setDropdownOpen((o) => !o);
  };

  const togglePick = (id) =>
    setPickedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleAssign = async () => {
    if (!hasTaskAuthority) return;
    if (!pickedIds.length) return;
    try {
      const res = await fetch(`${BASE_URL}/task/create-task-members/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ task_id: task.id, member_ids: pickedIds }),
      });

      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to add members to a task. You must have Task Authority Permission",
          "error",
        );
        return;
      }

      // ensure main task list (timeline/table + task group avatars) refreshes immediately
      if (typeof refetchTasks === "function") {
        refetchTasks(task.task_group || taskInfo?.task_group);
      }

      /* avoid duplicates */
      // setAssignees((prev) => {
      //   const already = new Set(prev.map((p) => p.id));
      //   const toAdd = siteMembers
      //     .filter(
      //       (m) => pickedIds.includes(m.member_id) && !already.has(m.member_id)
      //     )
      //     .map((m) => ({
      //       id: m.member_id,
      //       first_name: m.first_name,
      //       last_name:m.last_name,
      //       initials: initialsOf(m.first_name, m.last_name),
      //       color: "#5b6ad0",
      //     }));
      //   return [...prev, ...toAdd];
      // });
      setTrigger((prev) => prev + 1);
      setDropdownOpen(false);
      setPickedIds([]);
    } catch (e) {
      console.error("Assign members failed", e);
    }
  };

  /* click-away close for dropdown */
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setPickedIds([]);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const handleAssignApprovers = async () => {
    const approverIds = window
      .prompt("Enter approver IDs (comma-separated)")
      ?.split(",")
      .map(Number);
    if (!approverIds?.length) return;

    try {
      setLoading(true);
      await taskApi.assignTaskApprovers(task.id, approverIds);
      setApprovers(approverIds);
    } catch (error) {
      console.error("Failed to assign approvers:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- ADD SUBTASK ---------- */
  const handleAddSubtask = async (subtaskData) => {
    if (!subtaskData.subtask_name) return false;
    try {
      const res = await fetch(`${BASE_URL}/task/create-subtasks/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          task_id: task.id,
          subtask_name: subtaskData.subtask_name.trim(),
          subtask_details: subtaskData.subtask_details || "–",
          start_date: subtaskData.start_date || null,
          end_date: subtaskData.end_date || todayISO(),
          priority: subtaskData.priority || 3,
          assign_to: subtaskData.assign_to || [],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === false) throw new Error();

      const newSubtask = {
        id: data.data?.id ?? data.id,
        subtask_name: subtaskData.subtask_name,
        subtask_details: subtaskData.subtask_details,
        status: subtaskData.status || "Not started",
        start_date: subtaskData.start_date || "",
        end_date: subtaskData.end_date || todayISO(),
        priority: priorityText(subtaskData.priority || 3) || "Low",
      };

      setSubtasks((prev) => [...prev, newSubtask]);

      // Keep parent task dates in sync when the backend expands range based on subtask dates.
      await fetchTask(task.id);
      if (typeof refetchTasks === "function") {
        refetchTasks(task.task_group || taskInfo?.task_group);
      }

      setShowAddSubtaskModal(false);
      return true;
    } catch {
      addAlert("Could not create subtask.", "error");
      return false;
    }
  };

  /* ---------- ADD DOCUMENT ---------- */
  const handleAddDocument = async (fileToUpload = selectedFile) => {
    if (!fileToUpload) {
      addAlert("Please select a file to upload.", "warning");
      return;
    }
    if (!siteId) {
      addAlert(
        "Site context is missing. Please refresh and try again.",
        "error",
      );
      return;
    }

    const currentTaskId = task?.id ?? taskInfo?.id;
    if (!currentTaskId) {
      addAlert(
        "Task context is missing. Please reopen the task and try again.",
        "error",
      );
      return;
    }

    const formData = new FormData();
    formData.append("site_id", siteId);
    formData.append("task_id", currentTaskId);
    formData.append("task_ids", JSON.stringify([currentTaskId]));
    formData.append("file", fileToUpload);

    try {
      const response = await fetch(`${BASE_URL}/task/add-document/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const rawBody = await response.text();
      let data = {};
      if (rawBody) {
        try {
          data = JSON.parse(rawBody);
        } catch {
          data = {};
        }
      }

      if (!response.ok || data?.status === false) {
        const serverReturnedHtml500 =
          typeof rawBody === "string" && rawBody.includes("Server Error (500)");
        throw new Error(
          data?.detail ||
            data?.message ||
            (serverReturnedHtml500
              ? "Server error while uploading file. Please try again later."
              : "Failed to upload file"),
        );
      }

      await Promise.all([fetchTaskFiles(), fetchLinkedDocuments(task.id)]);
      setSelectedFile(null);
      addAlert("File uploaded successfully", "success");
    } catch (error) {
      console.error("Error uploading file:", error);
      addAlert(
        "Failed to upload file: " + (error.message || "Unknown error"),
        "error",
      );
    }
  };

  const handleFilesTabUpload = async (event) => {
    const fileToUpload = event?.target?.files?.[0];
    if (!fileToUpload || isUploading) return;

    setIsUploading(true);

    try {
      await handleAddDocument(fileToUpload);
    } catch (error) {
      // Error alert is already handled inside handleAddDocument.
    } finally {
      setIsUploading(false);
      if (event?.target) {
        event.target.value = "";
      }
    }
  };

  const handleFilePlusClick = () => {
    if (!showFileUpload) {
      setCanAddFiles(true);
    }
    setShowFileUpload((prev) => !prev);
    if (showFileUpload) {
      setSelectedFile(null); // Reset selected file when closing
    }
  };

  /* ---------- fetch task documents ---------- */
  const fetchTaskFiles = async () => {
    try {
      const response = await fetch(`${BASE_URL}/task/get-all-documents/`, {
        headers: authHeaders,
        method: "POST",
        body: JSON.stringify({ task_id: task.id }),
      });
      if (!response.ok) throw new Error("Failed to fetch files");
      const data = await response.json();
      const list = data.data || [];
      setFiles(list);
      return list;
    } catch (error) {
      console.error("Error fetching task files:", error);
      setFiles([]);
      return [];
    }
  };
  useEffect(() => {
    fetchTaskFiles();
  }, [task.id]);

  useEffect(() => {
    fetchLinkedDocuments(task.id);
  }, [task.id]);

  const handleDeleteDocument = async (documentId) => {
    if (!documentId || deletingDocumentId) return;

    setDeletingDocumentId(documentId);
    try {
      await taskApi.deleteTaskDocument(documentId);
      setFiles((prev) => prev.filter((doc) => doc.id !== documentId));
      addAlert("File deleted successfully", "success");
      if (typeof refetchTasks === "function") {
        refetchTasks(task.task_group || taskInfo?.task_group);
      }
    } catch (error) {
      console.error("Failed to delete task document:", error);
      addAlert(error.message || "Failed to delete file", "error");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const handleSubTaskComplete = async (subtask) => {
    try {
      const res = await fetch(
        `${BASE_URL}/task/update-subtask/${subtask.id}/`,
        {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify({
            is_completed: !subtask.is_completed ?? false,
            subtask_name: subtask.subtask_name,
            task_id: task.id,
          }),
        },
      );
      const data = await res.json();
      if (data.detail) {
        addAlert(
          "You do not have permission to edit task status, kindly ask your site admin to provide you with create taskgroup permission",
          "error",
        );
        return;
      }

      addAlert(
        `Subtask has been marked as ${
          subtask.is_completed ? "pending" : "complete"
        }`,
        "success",
      );
      setTrigger((prev) => prev + 1);
      // refetchTasks(task.task_group || taskInfo?.task_group);
      refetchSubtasks(task.task_group, task.id);
    } catch (e) {
      console.error("Update dates failed", e);
      addAlert("Could not update dates.", "error");
    }
  };

  const removeTaskMember = async (taskMemberId, taskId) => {
    if (!hasTaskAuthority) return;
    try {
      await fetch(`${BASE_URL}/task/create-task-members/`, {
        method: "DELETE",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task_id: taskId,
          member_ids: [taskMemberId], // Pass the taskMember.id in the member_ids array
        }),
      });

      setTrigger((prev) => prev + 1);
      refetchTasks(task.task_group || taskInfo?.task_group);
    } catch (e) {
      console.error("Delete task member failed", e);
    }
  };

  /* ---------- render helpers ---------- */
  const handleOpenSubtask = (subtask) => {
    if (!onOpenSubtask) return;
    onOpenSubtask({ ...subtask, task_id: subtask?.task_id ?? task.id });
    onClose?.();
  };

  const renderSubtasks = () => {
    return (
      <div className="m-4">
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (hasTaskAuthority) setShowAddSubtaskModal(true);
            }}
            disabled={!hasTaskAuthority}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold transition-all duration-200 ${
              hasTaskAuthority
                ? "bg-sm-primary text-white hover:bg-sm-primary/80"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
            title={
              hasTaskAuthority
                ? "Create a new subtask"
                : "Task authority permission is required"
            }
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Subtask
          </button>
        </div>

        <div className="border rounded">
          {loadingSub ? (
            <div className="p-4 text-sm text-gray-500">Loading…</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="p-2">Subtasks</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Start Date</th>
                  <th className="p-2">End Date</th>
                </tr>
              </thead>
              <tbody>
                {subtasks?.length ? (
                  subtasks.map((st) => (
                    <tr
                      key={st.id}
                      className="border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleOpenSubtask(st)}
                    >
                      <td className="p-2">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {st.subtask_name}
                          </span>
                        </div>
                      </td>
                      <td className="p-2">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            st.status === "Ongoing"
                              ? "bg-yellow-100 text-yellow-800"
                              : st.status === "Completed"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          {st.status}
                        </span>
                      </td>
                      <td className="p-2">{st.start_date || "-"}</td>
                      <td className="p-2">{st.end_date || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="p-4 text-sm text-gray-500">
                      No subtasks yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderUpdates = () => (
    <div className="flex-1 flex flex-col h-full pb-0">
      {/* Updates List */}
      <div className="flex-1 px-4 py-3" style={{ overflowY: "scroll" }}>
        {loadingUpd ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : updates.length ? (
          <div className="relative">
            <div className="absolute left-[1.125rem] top-2 bottom-2 w-px bg-gray-200" />
            {updates.map((u) => {
              // Helper to extract filename from URL
              const fileName = u.file
                ? u.file.split("?")[0].split("/").pop()
                : "Document";
              const isImage = u.file && isImageAttachment(u);
              const isVideo = u.file && isVideoUrl(u.file);

              // Format the date professionally
              const formattedDate = new Date(u.created_on).toLocaleString(
                "en-US",
                {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                },
              );

              return (
                <div
                  key={u.id}
                  className="group flex items-start space-x-3 mb-6 relative z-10"
                  onMouseEnter={() => setHoveredUpdateId(u.id)}
                  onMouseLeave={() =>
                    setHoveredUpdateId((prev) => (prev === u.id ? null : prev))
                  }
                >
                  {/* User Avatar */}
                  <div className="relative z-10">
                    {u.userpic ? (
                      <img
                        src={u.userpic}
                        alt="User"
                        className="w-9 h-9 rounded-full object-cover ring-1 ring-gray-200 bg-white"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#343C6A] text-white flex items-center justify-center text-xs font-semibold">
                        {u.created_by.includes("|")
                          ? getInitials(u.created_by.split("|")[1], "")
                          : getInitials(u.created_by, "")}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline space-x-2 mb-1">
                      <span className="text-sm font-bold text-gray-900">
                        {u.created_by.includes("|")
                          ? u.created_by.split("|")[1]
                          : u.created_by}
                      </span>
                      <span className="text-[11px] text-gray-400 font-medium">
                        {formattedDate}
                      </span>
                    </div>

                    {editingId === u.id ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          className="w-full border rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          rows={3}
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                        />
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={resetCommentBox}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSubmitComment}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md shadow-sm"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-700 leading-relaxed select-text whitespace-pre-wrap">
                          {u.comment}
                        </p>

                        {/* File Attachment Section */}
                        {u.file && (
                          <div className="mt-2">
                            {isImage ? (
                              <div className="relative inline-block group/img">
                                <img
                                  src={u.file}
                                  alt="Attachment"
                                  className="max-h-28 rounded-lg border border-gray-200 cursor-pointer hover:opacity-95 transition-opacity"
                                  onClick={() =>
                                    setSelectedViewerFile({
                                      url: u.file,
                                      name: fileName,
                                      type: "image",
                                    })
                                  }
                                />
                              </div>
                            ) : isVideo ? (
                              <div className="relative inline-block group/video">
                                <video
                                  src={u.file}
                                  className="max-h-28 rounded-lg border border-gray-200 cursor-pointer hover:opacity-95 transition-opacity"
                                  onClick={() =>
                                    setSelectedViewerFile({
                                      url: u.file,
                                      name: fileName,
                                      type: "video",
                                    })
                                  }
                                  muted
                                  preload="metadata"
                                />
                              </div>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedViewerFile({
                                    url: u.file,
                                    name: fileName,
                                    type: "file",
                                  });
                                }}
                                className="flex items-center p-2 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors w-fit max-w-full text-left"
                              >
                                <div className="bg-blue-100 p-2 rounded mr-3">
                                  <svg
                                    className="w-5 h-5 text-blue-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                    />
                                  </svg>
                                </div>
                                <div className="flex flex-col min-w-0 pr-4">
                                  <span className="text-xs font-medium text-gray-900 truncate">
                                    {fileName}
                                  </span>
                                  <span className="text-[10px] text-gray-500 uppercase">
                                    {fileName.split(".").pop()} File
                                  </span>
                                </div>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons - visible only on row hover */}
                  {editingId !== u.id && (
                    <div
                      className={`flex items-center space-x-1 transition-opacity ${
                        hoveredUpdateId === u.id
                          ? "opacity-100 pointer-events-auto"
                          : "opacity-0 pointer-events-none"
                      }`}
                    >
                      {hasImageAndComment(u) && (
                        <button
                          onClick={() => {
                            if (u.is_snag_created) {
                              addAlert(
                                "Snag has already been created for this update.",
                                "info",
                              );
                              return;
                            }
                            handleGenerateSnagFromUpdate(u);
                          }}
                          disabled={
                            creatingSnagUpdateId === u.id || u.is_snag_created
                          }
                          className="px-2 py-1 text-[11px] rounded-md border flex flex-row items-center gap-1 transition-colors text-[#3B0069] border-[#3B0069] hover:bg-[#F5EEF9] disabled:opacity-60 disabled:cursor-not-allowed"
                          title={
                            u.is_snag_created ? "Snag Created" : "Generate Snag"
                          }
                        >
                          {u.is_snag_created ? <GoAlert /> : null}
                          {creatingSnagUpdateId === u.id
                            ? "Generating..."
                            : u.is_snag_created
                              ? "Snag Created"
                              : "Generate Snag"}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(u.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 opacity-40">
            <svg
              className="w-12 h-12 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-sm font-medium">No updates yet</p>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Add Comment Section - Fixed at bottom */}
      {editingId === null && (
        <div className="border-t px-4 py-2 bg-white">
          {file && (
            <div className="mb-3 p-3 bg-[#F4F2EE] rounded-lg flex items-center justify-between border border-gray-200">
              <div className="flex items-center space-x-3 overflow-hidden">
                <div className="p-2 bg-white rounded border border-gray-200 flex items-center justify-center">
                  {file.type.startsWith("image/") ? (
                    <Image size={20} className="text-gray-600" />
                  ) : (
                    <FileText size={20} className="text-gray-600" />
                  )}
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB • Attached
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {file.type &&
                  file.type.startsWith &&
                  file.type.startsWith("image/") && (
                    <button
                      onClick={() => {
                        setAnnotatingFile(file);
                        setShowAnnotator(true);
                      }}
                      className="px-3 py-1 rounded-md bg-white border text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      title="Annotate image"
                    >
                      Annotate
                    </button>
                  )}
                <button
                  onClick={() => setFile(null)}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                  title="Remove attachment"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>
            </div>
          )}

          <div className="relative group">
            <textarea
              rows={3}
              className="w-full border-none rounded-t-lg p-3 text-sm bg-[#F4F2EE] focus:ring-0 resize-none min-h-[100px] transition-all placeholder-gray-500"
              placeholder="Write a message..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <div className="flex items-center justify-between px-3 py-2 bg-[#F4F2EE] rounded-b-lg border-t border-gray-200">
              <div className="flex items-center space-x-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                />
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                  accept="image/*"
                />
                <button
                  onClick={() => {
                    imageInputRef.current?.click();
                  }}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                  title="Add image"
                >
                  <Image size={20} />
                </button>
                <button
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                  title="Attach file"
                >
                  <Paperclip size={20} />
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleSubmitComment}
                  disabled={
                    (!newComment.trim() && !file) || isSubmittingComment
                  }
                  className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${
                    (!newComment.trim() && !file) || isSubmittingComment
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-[#0a66c2] text-white hover:bg-[#004182]"
                  }`}
                >
                  {isSubmittingComment ? "Sending..." : "Send"}
                </button>
                {/* <button className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors">
                      <MoreHorizontal size={20} />
                    </button> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderCompactFileGrid = () => {
    const allFiles = [
      ...files.map((f) => ({ ...f, type: "upload" })),
      ...linkedDocs.map((d) => ({ ...d, type: "linked", file: d.url })),
    ];

    if (allFiles.length === 0) return null;

    const displayFiles = allFiles.slice(0, 3);
    const hasMore = allFiles.length > 3;

    return (
      <div className="flex items-center gap-2 mt-2">
        <div className="flex -space-x-3 overflow-hidden">
          {displayFiles.map((f, i) => {
            const isImg = isImageUrl(f.file || f.url);
            return (
              <div
                key={i}
                className="flex items-center justify-center h-10 w-10 rounded-lg ring-2 ring-white bg-gray-50 border overflow-hidden"
              >
                {isImg ? (
                  <img
                    src={f.file || f.url}
                    alt="preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <FileText className="w-5 h-5 text-gray-500" />
                )}
              </div>
            );
          })}
        </div>
        {hasMore && (
          <div className="flex items-center justify-center h-10 w-10 rounded-full border border-gray-300 bg-white text-xs font-semibold text-gray-700">
            +{allFiles.length - 3}
          </div>
        )}
      </div>
    );
  };

  const renderFiles = () => {
    const allDocs = [
      ...files.map((f) => ({
        id: f.id,
        name: fileNameFromUrl(f.file),
        url: f.file,
        type: "upload",
        uploaded_on: f.uploaded_on,
        created_by: f.created_by,
      })),
      ...linkedDocs.map((d) => ({
        id: d.id,
        name: d.name,
        url: d.url,
        type: "linked",
        design_status: d.design_status,
        uploaded_on: d?.last_updated_on,
        created_by: d.created_by,
      })),
    ];

    return (
      <div className="m-4">
        <div className="mb-3 flex justify-end">
          <input
            ref={filesTabInputRef}
            type="file"
            className="hidden"
            onChange={handleFilesTabUpload}
          />
          <button
            type="button"
            onClick={() => filesTabInputRef.current?.click()}
            disabled={isUploading}
            className="inline-flex items-center gap-2 rounded-md border border-purple-600 px-3 py-1.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Paperclip className="h-4 w-4" />
            {isUploading ? "Uploading..." : "Upload File"}
          </button>
        </div>
        {allDocs.length ? (
          <div className="space-y-2">
            {allDocs.map((doc) => {
              const isImg = isImageUrl(doc.url);

              const formatDate = (dateString) => {
                if (!dateString) return "N/A";
                const date = new Date(dateString);
                return date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                });
              };

              return (
                <div
                  key={`${doc.type}-${doc.id}`}
                  className="flex items-center p-2 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center overflow-hidden flex-shrink-0 mr-3">
                    {isImg ? (
                      <img
                        src={doc.url}
                        alt={doc.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FileText className="w-5 h-5 text-gray-500" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {doc.name}
                      </h4>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          doc.type === "linked"
                            ? "bg-blue-50 text-blue-600 border border-blue-100"
                            : "bg-purple-50 text-purple-600 border border-purple-100"
                        }`}
                      >
                        {doc.type === "linked" ? "Linked" : "Uploaded"}
                      </span>
                      {doc.type === "linked" &&
                        doc.design_status &&
                        doc.design_status !== "approved" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100 capitalize">
                            {doc.design_status}
                          </span>
                        )}
                    </div>
                    <div className="flex items-center mt-0.5 text-[10px] text-gray-400 space-x-3">
                      {/* <span>{fileExtension}</span> */}
                      {doc.uploaded_on && (
                        <span>{formatDate(doc.uploaded_on)}</span>
                      )}
                      {doc.created_by && <span>{doc.created_by}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedViewerFile({
                          url: doc.url,
                          name: doc.name,
                          type: isImg ? "image" : "file",
                        })
                      }
                      className="text-xs font-medium text-purple-600 hover:text-purple-700"
                    >
                      View
                    </button>
                    <EllipsisMenu
                      items={[
                        {
                          key: `download-${doc.type}-${doc.id}`,
                          label: "Download",
                          icon: Download,
                          onClick: () => handleDownload(doc.url, doc.name),
                        },
                        ...(doc.type === "upload"
                          ? [
                              { type: "separator" },
                              {
                                key: `delete-${doc.id}`,
                                label:
                                  deletingDocumentId === doc.id
                                    ? "Deleting..."
                                    : "Delete",
                                icon: Trash2,
                                danger: true,
                                disabled: deletingDocumentId === doc.id,
                                onClick: () =>
                                  addAlert(
                                    `Delete ${doc.name}? This action cannot be undone.`,
                                    "confirm",
                                    {
                                      onConfirm: () =>
                                        handleDeleteDocument(doc.id),
                                    },
                                  ),
                              },
                            ]
                          : []),
                      ]}
                      buttonAriaLabel={`${doc.name} actions`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
            <Paperclip className="w-8 h-8 mb-2 opacity-20" />
            <p className="text-sm">No files or documents linked.</p>
          </div>
        )}
      </div>
    );
  };

  const fileModal = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[55vw] p-6">
        <div className="flex items-center justify-between mb-4">
          <div></div>
          <h3 className="text-lg text-[#232323] font-medium">
            Shared Documents
          </h3>
          <button
            onClick={() => setShowFileUpload(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-col justify-between rounded-lg border-[1px] border-[#78797A] items-start p-4">
          <div className="grid grid-cols-4 gap-4 w-full">
            {files.length ? (
              files.map((file) => {
                // Helper function to determine file type from URL or file.type
                const getFileType = (url) => {
                  const extension = url.split(".").pop().toLowerCase();
                  if (["png", "jpg", "jpeg", "gif", "bmp"].includes(extension))
                    return "image";
                  if (extension === "pdf") return "pdf";
                  return "other";
                };

                const fileType = file.type || getFileType(file.file);

                // Render preview based on file type
                const renderPreview = () => {
                  switch (fileType) {
                    case "image":
                      return (
                        <img
                          src={file.file}
                          alt="File preview"
                          className="w-full h-24 object-contain rounded"
                        />
                      );
                    case "pdf":
                      return (
                        <div className="w-full h-24 flex items-center justify-center rounded bg-gray-200">
                          <svg
                            className="w-12 h-12 text-gray-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <span className="text-xs text-gray-600">PDF</span>
                        </div>
                      );
                    default:
                      return (
                        <div className="w-full h-24 flex items-center justify-center rounded bg-gray-200">
                          <svg
                            className="w-12 h-12 text-gray-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <span className="text-xs text-gray-600">
                            {file.file.split(".").pop().toUpperCase()}
                          </span>
                        </div>
                      );
                  }
                };

                return (
                  <div
                    key={file.id}
                    className="flex flex-col cursor-pointer items-center justify-center bg-[#ECE6F0] p-3 rounded-lg aspect-square"
                  >
                    <div
                      onClick={() => handleDownload(file.file, file.url)}
                      className="w-full h-full flex flex-col items-center justify-center"
                    >
                      {renderPreview()}
                      <div className="flex items-center justify-between w-full mt-2">
                        <span className="text-sm text-gray-800 w-[85%] h-6 overflow-hidden text-ellipsis whitespace-nowrap">
                          {file.url}
                        </span>
                        <div onClick={(e) => e.stopPropagation()}>
                          <EllipsisMenu
                            items={[
                              {
                                key: `modal-download-${file.id}`,
                                label: "Download",
                                icon: Download,
                                onClick: () =>
                                  handleDownload(
                                    file.file,
                                    fileNameFromUrl(file.file),
                                  ),
                              },
                              { type: "separator" },
                              {
                                key: `modal-delete-${file.id}`,
                                label:
                                  deletingDocumentId === file.id
                                    ? "Deleting..."
                                    : "Delete",
                                icon: Trash2,
                                danger: true,
                                disabled: deletingDocumentId === file.id,
                                onClick: () =>
                                  addAlert(
                                    `Delete ${fileNameFromUrl(file.file)}? This action cannot be undone.`,
                                    "confirm",
                                    {
                                      onConfirm: () =>
                                        handleDeleteDocument(file.id),
                                    },
                                  ),
                              },
                            ]}
                            buttonAriaLabel={`${fileNameFromUrl(file.file)} actions`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 col-span-4">
                No documents uploaded yet
              </p>
            )}
          </div>
          {canAddFiles && (
            <div className="mt-4 w-full">
              {/* Show selected file name if a file is selected */}
              {selectedFile && (
                <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <svg
                        className="w-5 h-5 text-gray-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm text-gray-700 font-medium">
                        {selectedFile.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                {/* File selection input */}
                <input
                  type="file"
                  id="file-upload"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setSelectedFile(e.target.files[0]);
                    }
                  }}
                  className="hidden"
                />

                {/* Add File button */}
                <label
                  htmlFor="file-upload"
                  className="px-3 py-1 bg-transparent border-[1px] border-purple-600 text-purple-600 rounded flex items-center space-x-2 cursor-pointer hover:bg-purple-50 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  <span>Add File</span>
                </label>

                {/* Upload button - only show when file is selected */}
                {selectedFile && (
                  <button
                    onClick={() => {
                      if (isUploading) return; // Prevent multiple clicks
                      setIsUploading(true);
                      handleAddDocument().finally(() => setIsUploading(false));
                    }}
                    disabled={isUploading}
                    className="px-4 py-1 bg-purple-600 text-white rounded flex items-center space-x-2 hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <span>Upload</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm }) => (
    <DeleteConfirmationModal
      open={isOpen}
      title="Delete Task"
      message={
        isTaskLinked
          ? `This will also delete BoQ Item: ${task.name}\nAre you sure you want to delete this task? This action cannot be undone.`
          : `Are you sure you want to delete this task? This action cannot be undone.`
      }
      onConfirm={onConfirm}
      onCancel={onClose}
      requireConfirmationText={isTaskLinked ? task.name : null}
      isBoQLinked={isTaskLinked}
      itemType="Task"
      itemName={task.name}
    />
  );

  const [refreshing, setRefreshing] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);

  // Handle annotated image save from the annotator modal.
  const handleAnnotatorSave = async (result) => {
    try {
      let newFile = null;
      // result can be a dataURL string, Blob, or File
      if (typeof result === "string" && result.startsWith("data:")) {
        const res = await fetch(result);
        const blob = await res.blob();
        const fileName = annotatingFile?.name || `annotated_${Date.now()}.png`;
        newFile = new File([blob], fileName, { type: blob.type });
      } else if (result instanceof Blob) {
        const fileName = annotatingFile?.name || `annotated_${Date.now()}.png`;
        newFile = new File([result], fileName, {
          type: result.type || "image/png",
        });
      } else if (result instanceof File) {
        newFile = result;
      }

      if (newFile) {
        setFile(newFile);
        setAnnotatingFile(null);
      }
    } catch (e) {
      console.error("Failed to process annotated image", e);
    } finally {
      setShowAnnotator(false);
    }
  };

  const handleUpdateTaskName = async (editedName) => {
    if (!hasTaskAuthority) return;
    if (!editedName.trim()) {
      addAlert("Task name cannot be empty", "error");
      return;
    }
    try {
      await taskApi.updateTask(task.id, { task_name: editedName });
      // addAlert("Task name updated successfully", "success");
      setShowEditNameModal(false);
      onRefresh();
    } catch (error) {
      console.error("Failed to update task name:", error);
      addAlert("Failed to update task name", "error");
    }
  };

  const EditNameModal = ({ isOpen, onClose, onSave }) => {
    const [value, setValue] = useState(taskInfo?.task_name);

    return (
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[60] ${
          isOpen ? "block" : "hidden"
        }`}
      >
        <div className="bg-white rounded-lg shadow-lg w-96 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Edit Task Name
          </h3>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 mb-6 focus:outline-none focus:border-purple-500"
            placeholder="Enter task name"
          />
          <div className="flex justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                handleUpdateTaskName(value);
                onClose();
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchTasks(task.task_group || taskInfo?.task_group);
      await refetchSubtasks(task.task_group, task.id);
      await fetchTaskFiles();
      await fetchLinkedDocuments(task.id);
      await fetchTask(task.id);
      setTrigger((prev) => prev + 1);
      // addAlert("Task details refreshed successfully", "success");
    } catch (error) {
      console.error("Failed to refresh task details:", error);
      addAlert("Failed to refresh task details", "error");
    } finally {
      setRefreshing(false);
    }
  };

  const subtaskConstraints = React.useMemo(() => {
    if (subtasks.length === 0) return { minDate: null, maxDate: null };
    const starts = subtasks
      .map((st) => st.start_date)
      .filter(Boolean)
      .map((d) => new Date(d));

    const ends = subtasks
      .map((st) => st.end_date)
      .filter(Boolean)
      .map((d) => new Date(d));

    return {
      minDate: starts.length > 0 ? new Date(Math.min(...starts)) : null,
      maxDate: ends.length > 0 ? new Date(Math.max(...ends)) : null,
    };
  }, [subtasks]);

  /* ---------- main render ---------- */
  return (
    <div
      className={`fixed inset-y-0 right-0 w-[45vw] bg-white shadow-lg z-50 flex flex-col border-l-[1px] border-[#78797A] transition-transform duration-300 ease-in-out ${
        isVisible ? "translate-x-0" : "translate-x-full"
      }`}
    >
      {/* header */}
      {/* header */}
      <div className="flex flex-col w-full bg-white border-b border-gray-100 p-3">
        {/* Top Row: Title & Actions */}
        <div className="flex justify-between items-start gap-4">
          {/* Left: Title Area */}
          <div className="flex items-center flex-1 min-w-0 max-w-[75%]">
            <div className="flex items-center min-w-0 flex-1">
              <h2
                onClick={() => {
                  if (canEditTaskMeta) setShowEditNameModal(true);
                }}
                className={`text-xl font-medium text-gray-900 transition-colors truncate leading-tight tracking-tight mr-3 min-w-0 ${
                  canEditTaskMeta
                    ? "cursor-pointer hover:text-purple-700"
                    : "cursor-default"
                }`}
                title={canEditTaskMeta ? "Click to edit task name" : ""}
              >
                {taskInfo?.task_name}
              </h2>
            </div>

            <EditNameModal
              isOpen={canEditTaskMeta && showEditNameModal}
              onClose={() => setShowEditNameModal(false)}
              onSave={handleUpdateTaskName}
            />
          </div>
          {/* Right: Actions Toolbar */}
          <div className="flex items-center space-x-2 pl-4">
            {/* Task Actions Menu */}
            <div className="relative" ref={taskActionsRef}>
              {hasTaskAuthority && (
                <button
                  onClick={() => setTaskActionsOpen((prev) => !prev)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  title="Task actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              )}

              {/* {taskActionsOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                  {hasTaskAuthority && (
                    <button
                      onClick={() => {
                        setTaskActionsOpen(false);
                        setShowEditNameModal(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <MdEdit className="w-4 h-4" />
                      Edit task
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setTaskActionsOpen(false);
                      handleTaskDelete();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <MdDelete className="w-4 h-4" />
                    Delete task
                  </button>
                </div>
              )} */}
              {taskActionsOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
                  {hasTaskAuthority && (
                    <button
                      onClick={() => {
                        setTaskActionsOpen(false);
                        setShowEditNameModal(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <MdEdit className="w-4 h-4" />
                      Edit task
                    </button>
                  )}
                  {hasTaskAuthority && (
                    <button
                      onClick={() => {
                        setTaskActionsOpen(false);
                        handleTaskDelete();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <MdDelete className="w-4 h-4" />
                      Delete task
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Refresh */}
            <button
              onClick={onRefresh}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Refresh"
            >
              <FaSyncAlt
                className={`w-3.5 h-3.5 ${
                  refreshing ? "animate-spin text-purple-600" : ""
                }`}
              />
            </button>

            {/* Close Panel */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Bottom Row: Meta Info */}
        {!showEditNameModal && (task?.created_by || task?.createdAt) && (
          <div className="flex items-center text-xs text-gray-400 space-x-3 mt-1">
            <div className="flex items-center">
              <span className="font-medium text-gray-500 mr-1">
                Created by:
              </span>
              {task.created_by || "Unknown"} | {task.createdAt || "—"}
            </div>
          </div>
        )}
      </div>

      {/* info section */}
      <div className="grid grid-cols-3 gap-4 p-4 pt-2 border-b relative z-30">
        {/* Members Card */}
        <div className="flex flex-col items-start justify-between relative border border-[#3B0069] rounded-lg px-2 pt-2 pb-3">
          <label className="text-sm text-black ">Task Members</label>
          <div className="flex items-center relative">
            {assignees.slice(0, 3).map((a, index) =>
              a?.userpic ? (
                <img
                  key={a.id}
                  src={a.userpic}
                  alt={`${a.first_name} ${a.last_name}`}
                  title={`${a.first_name} ${a.last_name}`}
                  style={{ marginLeft: index > 0 ? "-4px" : "0px" }}
                  className="w-7 h-7 rounded-full border-[1px] border-white object-cover"
                />
              ) : (
                <div
                  key={a.id}
                  style={{
                    marginLeft: index > 0 ? "-4px" : "0px",
                  }}
                  className="w-7 h-7 rounded-full border-[1px] border-white flex items-center justify-center bg-[#343C6A] text-white text-[11px]"
                  title={`${a.first_name} ${a.last_name}`}
                >
                  {a.initials}
                </div>
              ),
            )}
            {assignees.length > 3 && (
              <div
                style={{
                  marginLeft: "-4px",
                }}
                className="w-7 h-7 rounded-full border-[1px] border-white flex items-center justify-center bg-[#343C6A] text-white text-[11px]"
              >
                +{assignees.length - 3}
              </div>
            )}

            {hasTaskAuthority && (
              <button
                onClick={handlePlusClick}
                className="w-7 h-7 rounded-full flex items-center justify-center bg-white border border-gray-300 text-gray-500 text-[13px] hover:bg-gray-100"
                title="Assign members"
              >
                +
              </button>
            )}
            {hasTaskAuthority && dropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-40 overflow-hidden animate-in slide-in-from-top-2 duration-200"
              >
                {/* Header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Assign Members
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {assignees.length} assigned • {pickedIds.length} selected
                  </p>
                </div>

                {/* Scrollable Content */}
                <div className="max-h-64 overflow-y-auto">
                  {/* Currently Assigned Members */}
                  {assignees.length > 0 && (
                    <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                      <div className="text-xs font-medium text-blue-700 mb-2 uppercase tracking-wide">
                        Currently Assigned
                      </div>
                      <div className="space-y-1">
                        {assignees.map((taskMember) => (
                          <div
                            key={taskMember.id}
                            className="flex items-center justify-between py-2 px-2 rounded-lg bg-white border border-blue-200 group hover:border-blue-300 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              {taskMember?.userpic ? (
                                <img
                                  src={taskMember.userpic}
                                  alt={`${taskMember.first_name} ${taskMember.last_name}`}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-xs font-medium text-blue-700">
                                    {taskMember.first_name?.[0]}
                                    {taskMember.last_name?.[0]}
                                  </span>
                                </div>
                              )}
                              <span className="text-sm font-medium text-gray-900">
                                {taskMember.first_name} {taskMember.last_name}
                              </span>
                            </div>
                            <button
                              onClick={() =>
                                removeTaskMember(taskMember.member_id, task.id)
                              }
                              className="flex items-center justify-center p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200 "
                              title="Remove assignee"
                            >
                              <MdDelete color="red" width={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Members */}
                  <div className="px-4 py-2">
                    {siteMembers.length ? (
                      <>
                        <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                          Available Members
                        </div>
                        <div className="space-y-1">
                          {siteMembers
                            .filter(
                              (m) =>
                                !assignees.some(
                                  (assignee) =>
                                    assignee.member_id === m.member_id,
                                ),
                            )
                            .map((m) => (
                              <label
                                key={m.member_id}
                                className="flex items-center py-2 px-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
                              >
                                <input
                                  type="checkbox"
                                  style={{
                                    accentColor: "#3B0069",
                                  }}
                                  className="mr-3 w-4 h-4 text-purple-600 border-gray-300 rounded "
                                  checked={pickedIds.includes(m.member_id)}
                                  onChange={() => togglePick(m.member_id)}
                                />
                                <div className="flex items-center space-x-3">
                                  {m?.userpic ? (
                                    <img
                                      src={m.userpic}
                                      alt={`${m.first_name} ${m.last_name}`}
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                                      <span className="text-xs font-medium text-gray-600">
                                        {m.first_name?.[0]}
                                        {m.last_name?.[0]}
                                      </span>
                                    </div>
                                  )}
                                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                                    {m.first_name} {m.last_name}
                                  </span>
                                </div>
                              </label>
                            ))}
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                          <svg
                            className="w-6 h-6 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                            />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">
                          No members available
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          All members are already assigned
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {pickedIds.length > 0 && (
                      <span className="text-purple-600 font-medium">
                        {pickedIds.length} member
                        {pickedIds.length !== 1 ? "s" : ""} selected
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        /* Clear selection logic */
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                      disabled={!pickedIds.length}
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleAssign}
                      className="px-4 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs font-medium rounded-lg shadow-sm hover:from-purple-700 hover:to-purple-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 flex items-center space-x-1"
                      disabled={!pickedIds.length}
                    >
                      <span>Assign</span>
                      {pickedIds.length > 0 && (
                        <span className="bg-white/20 rounded-full px-1.5 py-0.5 text-xs">
                          {pickedIds.length}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Due Date Card */}
        <div className="flex flex-col items-start justify-between border border-[#3B0069] rounded-lg px-2 pt-2 pb-4">
          <label className="text-sm text-black">Date Range</label>
          {hasTaskAuthority ? (
            <div className="relative">
              <CalendarPicker
                startDate={startDate}
                endDate={endDate}
                onDateChange={handleDateChange}
                placeholder="Select dates"
                minDate={
                  subtaskConstraints.minDate?.toISOString().split("T")[0] ??
                  null
                }
                maxDate={
                  subtaskConstraints.maxDate?.toISOString().split("T")[0] ??
                  null
                }
                subtasks={subtasks}
                compact
              />

              {/* {subtaskConstraints.minDate && subtaskConstraints.maxDate && (
                <p className="text-xs text-gray-500 mt-1">
                  Select Date range inclusive of subtasks
                </p>
              )} */}
              {isUpdatingDates && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full rounded-md border border-gray-200 bg-gray-50 px-2 py-2 text-sm text-gray-600">
              {startDate || endDate
                ? `${startDate ? formatTaskDate(startDate) : "No start date"} - ${endDate ? formatTaskDate(endDate) : "No end date"}`
                : "No date range set"}
            </div>
          )}
        </div>
        {/* Linked Documents (Designs linked to this task) */}
        <div className="flex flex-col border border-[#3B0069] rounded-lg px-2 py-2">
          <div className="w-full flex items-center justify-between">
            <label className="text-sm text-black">Files</label>
            <button
              onClick={() => setActiveTab("Files")}
              className="text-xs text-purple-600 hover:underline"
            >
              See all
            </button>
          </div>

          <div className="w-full">
            {linkedDocsLoading ? (
              <div className="text-xs text-gray-400 py-2">Loading…</div>
            ) : (
              renderCompactFileGrid()
            )}
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="flex border-b border-t relative z-10 bg-white">
        {["Updates", "Subtasks", "Files"].map((t) => (
          <button
            key={t}
            className={`px-8 py-2 text-sm ${
              activeTab === t
                ? "text-purple-600 border-b-2 border-purple-600"
                : "text-gray-600"
            }`}
            onClick={() => setActiveTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* tab content */}
      <div className="flex-1 overflow-auto">
        {activeTab === "Updates"
          ? renderUpdates()
          : activeTab === "Subtasks"
            ? renderSubtasks()
            : activeTab === "Files"
              ? renderFiles()
              : null}
      </div>

      {/* Updates modal */}

      {/* Add Subtask Modal */}
      {showAddSubtaskModal && (
        <AddSubtaskModal
          onClose={() => setShowAddSubtaskModal(false)}
          handleSubmit={handleAddSubtask}
          taskId={task.id}
          parentStartDate={startDate || task.start_date || task.startDate}
          parentDueDate={endDate || task.end_date || task.endDate}
        />
      )}

      {showFileUpload && fileModal}

      <ConfirmDeleteModal
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={onDeleteTask}
        isOpen={showConfirmDelete}
      />

      <EditNameModal
        isOpen={showEditNameModal}
        onClose={() => setShowEditNameModal(false)}
      />

      {/* Assign Approvers */}
      {/* File Viewer Modal */}
      {showAnnotator && annotatingFile && (
        <ImageAnnotatorModal
          imageFile={annotatingFile}
          onClose={() => {
            setShowAnnotator(false);
            setAnnotatingFile(null);
          }}
          onSave={handleAnnotatorSave}
        />
      )}
      {selectedViewerFile &&
        (selectedViewerFile.type === "video" ? (
          <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4">
            <div className="w-full max-w-5xl bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <p className="text-sm font-medium text-gray-900 truncate pr-3">
                  {selectedViewerFile.name}
                </p>
                <button
                  onClick={() => setSelectedViewerFile(null)}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="bg-black flex items-center justify-center max-h-[80vh]">
                <video
                  src={selectedViewerFile.url}
                  controls
                  autoPlay
                  className="w-full max-h-[80vh]"
                />
              </div>
            </div>
          </div>
        ) : (
          <FileViewer
            fileUrl={selectedViewerFile.url}
            fileName={selectedViewerFile.name}
            onClose={() => setSelectedViewerFile(null)}
          />
        ))}
    </div>
  );
};

export default TaskDetailComponent;
